import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Scale, Pencil, Check, X } from "lucide-react";
import { getWeighTolerance, setWeighTolerance } from "@/lib/api";

// One crane-weighing tolerance (± kg) for ALL RM or ALL SFG — set by the Director.
export const WeighToleranceBar = ({ kind, canEdit }) => {
  const qc = useQueryClient();
  const key = kind === "rm" ? "rm_kg" : "sfg_kg";
  const label = kind === "rm" ? "all RM" : "all SFG";
  const { data } = useQuery({ queryKey: ["weigh-tolerance"], queryFn: getWeighTolerance });
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const current = data?.[key];

  const mut = useMutation({
    mutationFn: (v) => setWeighTolerance({ [key]: v }),
    onSuccess: (d) => { qc.setQueryData(["weigh-tolerance"], d); setEditing(false); toast.success(`Tolerance for ${label} set to ± ${d[key]} kg`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't save"),
  });
  const save = () => {
    const v = parseFloat(val);
    if (!Number.isFinite(v) || v < 0) { toast.error("Enter 0 or more kg"); return; }
    mut.mutate(v);
  };

  return (
    <div data-testid={`weigh-tolerance-bar-${kind}`} className="mb-4 flex flex-wrap items-center gap-3 border border-sky-200 bg-sky-50 rounded-sm px-4 py-3">
      <Scale className="h-4 w-4 text-sky-700" />
      <span className="text-sm text-sky-900">Crane weighing tolerance for <b>{label}</b> (RM / SFG Inward):</span>
      {editing ? (
        <span className="inline-flex items-center gap-1.5">
          ±
          <input data-testid={`weigh-tolerance-input-${kind}`} autoFocus type="number" step="0.1" min="0" value={val}
            onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()}
            className="w-24 h-9 px-2 border border-sky-300 rounded-sm font-mono text-sm bg-white" />
          kg
          <button data-testid={`weigh-tolerance-save-${kind}`} onClick={save} disabled={mut.isPending} className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-slate-900 text-white"><Check className="h-4 w-4" /></button>
          <button onClick={() => setEditing(false)} className="h-9 w-9 inline-flex items-center justify-center rounded-sm border border-slate-300 bg-white"><X className="h-4 w-4" /></button>
        </span>
      ) : (
        <>
          <span data-testid={`weigh-tolerance-value-${kind}`} className={`font-mono font-bold text-lg ${current == null ? "text-amber-700" : "text-sky-900"}`}>
            {!data ? "…" : current == null ? "Not set — weights must match exactly" : `± ${current} kg`}
          </span>
          {canEdit && (
            <button data-testid={`weigh-tolerance-edit-${kind}`} onClick={() => { setVal(current ?? ""); setEditing(true); }}
              className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-sky-800 hover:text-slate-900">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          )}
        </>
      )}
      {data?.updated_by && !editing && <span className="text-xs text-sky-700/70 ml-auto">Last set by {data.updated_by}</span>}
    </div>
  );
};
